// @ts-nocheck
import { useState } from "react";
import { Plus, X } from "lucide-react";
import { SubSection, FieldLabel, inputClass } from "./SectionWrapper";
import { EventData, Venue } from "@/types/events";
import { supabase } from "@/integrations/supabase/client";

interface Props {
  event: EventData;
  updateEvent: (updates: Partial<EventData>) => void;
  venues: Venue[];
  setVenues: React.Dispatch<React.SetStateAction<Venue[]>>;
}

const emptyVenue = { name: "", address: "", city: "", state: "", zip_code: "", phone: "", website: "" };

export function VenuesSection({ event, updateEvent, venues, setVenues }: Props) {
  const [showNewVenue, setShowNewVenue] = useState(false);
  const [newVenue, setNewVenue] = useState(emptyVenue);
  const [saving, setSaving] = useState(false);

  const mainVenue = venues.find((v) => v.id === event.main_venue_id);
  const additionalIds = event.additional_venue_ids ?? [];

  // De-duplicate venues that share the same name/city/state, but always keep
  // any venue that is currently selected so existing selections never vanish.
  const dedupedVenues = (() => {
    const selected = new Set([event.main_venue_id, ...additionalIds].filter(Boolean) as string[]);
    const seen = new Set<string>();
    const result: Venue[] = [];
    for (const v of venues) {
      const key = `${v.name?.trim().toLowerCase()}|${v.city?.trim().toLowerCase()}|${v.state?.trim().toLowerCase()}`;
      if (selected.has(v.id)) {
        result.push(v);
        seen.add(key);
        continue;
      }
      if (seen.has(key)) continue;
      seen.add(key);
      result.push(v);
    }
    return result;
  })();

  // Venue Details rows (event_venue_info), one per selected venue; saved with the event.
  const selectedVenueIds = [event.main_venue_id, ...additionalIds]
    .filter(Boolean)
    .filter((id, i, a) => a.indexOf(id) === i) as string[];
  const venueInfo: any[] = (event as any)._venue_info ?? [];
  const infoFor = (id: string) => venueInfo.find((r) => r.venue_id === id) || { venue_id: id };
  const setInfo = (id: string, patch: any) => {
    const exists = venueInfo.some((r) => r.venue_id === id);
    const next = exists
      ? venueInfo.map((r) => (r.venue_id === id ? { ...r, ...patch } : r))
      : [...venueInfo, { venue_id: id, divisions: "", doors_open: "", notes: "", ...patch }];
    updateEvent({ _venue_info: next } as any);
  };

  const formatVenue = (v: Venue) =>
    `${v.name}${v.city ? ` - ${v.city}` : ""}${v.state ? `, ${v.state}` : ""}`;

  const handleAddVenue = async () => {
    if (!newVenue.name.trim()) return;
    setSaving(true);
    const { data, error } = await supabase
      .from("venues")
      .insert({
        name: newVenue.name,
        address: newVenue.address,
        city: newVenue.city,
        state: newVenue.state,
        zip_code: newVenue.zip_code,
      })
      .select()
      .single();

    if (!error && data) {
      setVenues((prev) => [...prev, data]);
      updateEvent({ additional_venue_ids: [...additionalIds, data.id] });
      setNewVenue(emptyVenue);
      setShowNewVenue(false);
    }
    setSaving(false);
  };

  const toggleAdditionalVenue = (venueId: string) => {
    if (additionalIds.includes(venueId)) {
      updateEvent({ additional_venue_ids: additionalIds.filter((id) => id !== venueId) });
    } else {
      updateEvent({ additional_venue_ids: [...additionalIds, venueId] });
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <FieldLabel>Tournament Headquarters</FieldLabel>
        <select
          value={event.main_venue_id ?? ""}
          onChange={(e) => updateEvent({ main_venue_id: e.target.value })}
          className={inputClass}
        >
          <option value="">Select main venue...</option>
          {dedupedVenues.map((v) => (
            <option key={v.id} value={v.id}>
              {formatVenue(v)}
            </option>
          ))}
        </select>
        {mainVenue && (
          <p className="text-xs text-muted-foreground mt-1">
            {mainVenue.address && `${mainVenue.address}, `}
            {mainVenue.city}, {mainVenue.state} {mainVenue.zip_code}
          </p>
        )}
      </div>

      <SubSection title="Additional Venues">
        <div className="space-y-2 max-h-48 overflow-y-auto">
          {dedupedVenues
            .filter((v) => v.id !== event.main_venue_id)
            .map((v) => (
              <label key={v.id} className="flex items-center gap-2 p-2 rounded hover:bg-secondary cursor-pointer">
                <input
                  type="checkbox"
                  checked={additionalIds.includes(v.id)}
                  onChange={() => toggleAdditionalVenue(v.id)}
                  className="rounded"
                />
                <span className="text-sm">{formatVenue(v)}</span>
              </label>
            ))}
        </div>

        {additionalIds.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-2">
            {additionalIds.map((id) => {
              const v = venues.find((ve) => ve.id === id);
              if (!v) return null;
              return (
                <span
                  key={id}
                  className="inline-flex items-center gap-1 px-2 py-1 bg-primary/10 text-primary rounded-full text-xs"
                >
                  {v.name}
                  <button onClick={() => toggleAdditionalVenue(id)}>
                    <X className="w-3 h-3" />
                  </button>
                </span>
              );
            })}
          </div>
        )}
      </SubSection>

      {selectedVenueIds.length > 0 && (
        <SubSection title="Venue Details">
          <div className="space-y-3">
            {selectedVenueIds.map((id) => {
              const v = venues.find((ve) => ve.id === id);
              const r = infoFor(id);
              return (
                <div key={id} className="p-3 border border-border rounded-lg space-y-2">
                  <div className="text-sm font-semibold">{v ? v.name : "Venue"}</div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                    <div>
                      <FieldLabel>Age Divisions</FieldLabel>
                      <input type="text" className={inputClass} placeholder="e.g. 15u-17u Boys"
                        value={r.divisions ?? ""} onChange={(e) => setInfo(id, { divisions: e.target.value })} />
                    </div>
                    <div>
                      <FieldLabel>Doors Open</FieldLabel>
                      <input type="text" className={inputClass} placeholder="e.g. Fri 5pm, Sat/Sun 8am"
                        value={r.doors_open ?? ""} onChange={(e) => setInfo(id, { doors_open: e.target.value })} />
                    </div>
                    <div>
                      <FieldLabel>Notes</FieldLabel>
                      <input type="text" className={inputClass}
                        value={r.notes ?? ""} onChange={(e) => setInfo(id, { notes: e.target.value })} />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </SubSection>
      )}

      {!showNewVenue ? (
        <button
          onClick={() => setShowNewVenue(true)}
          className="flex items-center gap-2 text-sm text-primary hover:text-primary/80 font-medium"
        >
          <Plus className="w-4 h-4" />
          Add New Venue
        </button>
      ) : (
        <div className="p-4 border border-border rounded-lg space-y-3 bg-secondary/30">
          <h4 className="text-sm font-semibold">New Venue</h4>
          <div>
            <FieldLabel required>Venue Name</FieldLabel>
            <input
              type="text"
              value={newVenue.name}
              onChange={(e) => setNewVenue({ ...newVenue, name: e.target.value })}
              className={inputClass}
            />
          </div>
          <div>
            <FieldLabel>Address</FieldLabel>
            <input
              type="text"
              value={newVenue.address}
              onChange={(e) => setNewVenue({ ...newVenue, address: e.target.value })}
              className={inputClass}
            />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <FieldLabel>City</FieldLabel>
              <input
                type="text"
                value={newVenue.city}
                onChange={(e) => setNewVenue({ ...newVenue, city: e.target.value })}
                className={inputClass}
              />
            </div>
            <div>
              <FieldLabel>State</FieldLabel>
              <input
                type="text"
                maxLength={2}
                value={newVenue.state}
                onChange={(e) => setNewVenue({ ...newVenue, state: e.target.value })}
                className={inputClass}
              />
            </div>
            <div>
              <FieldLabel>ZIP</FieldLabel>
              <input
                type="text"
                value={newVenue.zip_code}
                onChange={(e) => setNewVenue({ ...newVenue, zip_code: e.target.value })}
                className={inputClass}
              />
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleAddVenue}
              disabled={saving || !newVenue.name.trim()}
              className="px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:opacity-90 disabled:opacity-50"
            >
              {saving ? "Saving..." : "Save Venue"}
            </button>
            <button
              onClick={() => {
                setShowNewVenue(false);
                setNewVenue(emptyVenue);
              }}
              className="px-4 py-2 bg-secondary text-foreground rounded-lg text-sm font-medium"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}